/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect } from 'react';

import type { DescriptionPage_PremiumExperienceVariant as PremiumExperienceVariant } from '__generated__/graphql-types';
import { compose } from 'recompose';

import Imgix from 'js/components/Imgix';

import { Link, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { getLandingPageLink, logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import type { Partners } from 'bundles/coursera-plus/utils/subscriptionTiersUtils';
import {
  generateSubscriptionTiersImpression,
  getIsStandaloneCourseEligibleForTiersEnroll,
  initSubscriptionTiersFullStory,
} from 'bundles/coursera-plus/utils/subscriptionTiersUtils';
import withEnrollment from 'bundles/enroll/components/xdp/withEnrollment';
import type { PropsFromWithEnrollment } from 'bundles/enroll/components/xdp/withEnrollment';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { useTracker } from 'bundles/page/lib/event-pulse/react';
import type { PropsFromWithPromotionInfo } from 'bundles/promotions/components/withPromotionInfo';
import withPromotionInfo from 'bundles/promotions/components/withPromotionInfo';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

import _t from 'i18n!nls/xdp';

type PropsFromCaller = {
  isSubscribedToCPlusOrLite?: boolean;
  isSubscribedToCourseraLite?: boolean;
  hasSpecialization: boolean;
  disableCTA?: boolean;
  partners?: Partners;
  courseId?: string;
  s12nId?: string;
  premiumExperienceVariant?: PremiumExperienceVariant | null;
};

type PropsToComponent = PropsFromCaller & PropsFromWithEnrollment & PropsFromWithPromotionInfo;

const styles = {
  container: (theme: Theme) => css`
    display: flex;
    align-items: center;
    margin-top: ${theme.spacing(8)};
  `,
  courseraPlusLogoImage: (theme: Theme) => css`
    position: relative;
    top: 1px;
    border-radius: 4px;
    margin-right: ${theme.spacing(4)};
  `,
  learnMoreLink: (theme: Theme) => css`
    text-decoration-color: ${theme.palette.black[500]};
  `,
};

export const HeroHorizontalCourseraPlusOrLiteCta = ({
  isSubscribedToCPlusOrLite,
  isSubscribedToCourseraLite,
  hasSpecialization,
  disableCTA,
  partners,
  enrollmentAvailableChoices,
  premiumExperienceVariant,
  promotionEligibilities,
}: PropsToComponent) => {
  const eventingProductData = useEventingProductData();
  const isStandaloneCourseEligibleForTiersEnroll = getIsStandaloneCourseEligibleForTiersEnroll({
    hasSpecialization,
    premiumExperienceVariant,
    enrollmentAvailableChoices,
  });
  const track = useTracker();
  const hasPromotion = Boolean(promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : undefined);

  useEffect(() => {
    generateSubscriptionTiersImpression({
      hasSpecialization,
      partners,
      isStandaloneCourseEligibleForTiersEnroll,
      hasPromotion,
    });

    if (!isSubscribedToCPlusOrLite) {
      initSubscriptionTiersFullStory();
    }
  }, [isSubscribedToCPlusOrLite, hasSpecialization, partners, isStandaloneCourseEligibleForTiersEnroll, hasPromotion]);

  const includedWithCourseraPlusOrLiteText = isSubscribedToCourseraLite ? (
    <Typography2 component="p" variant="bodySecondary" data-e2e="hero-coursera-lite-learn-more">
      {_t('Included with Coursera Lite')}
    </Typography2>
  ) : (
    <Typography2 component="p" variant="bodySecondary" data-e2e="hero-coursera-plus-learn-more">
      {_t('Included with Coursera Plus')}
    </Typography2>
  );

  const onClick = () => {
    track('click_button', {
      button: { name: 'description_page_c_plus_learn_more' },
      pageSection: {
        sectionName: 'hero_banner',
      },
      ...eventingProductData,
    });
  };

  return (
    <div css={styles.container} data-e2e="hero-coursera-plus-cta">
      {isSubscribedToCourseraLite ? (
        <Imgix src={logo.LITE_PILL_BLUE} maxHeight={16} alt={_t('Coursera Lite')} css={styles.courseraPlusLogoImage} />
      ) : (
        <Imgix src={logo.PLUS_PILL_BLUE} maxHeight={16} alt={_t('Coursera Plus')} css={styles.courseraPlusLogoImage} />
      )}
      {isSubscribedToCPlusOrLite || disableCTA ? (
        includedWithCourseraPlusOrLiteText
      ) : (
        <Link
          component={TrackedLink2}
          href={getLandingPageLink()}
          trackingName="coursera_plus_banner"
          typographyVariant="bodyPrimary"
          css={styles.learnMoreLink}
          aria-label={_t('Included with Coursera Plus')}
          onClick={onClick}
        >
          {includedWithCourseraPlusOrLiteText}
        </Link>
      )}
    </div>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(
  withPromotionInfo(),
  withEnrollment()
)(HeroHorizontalCourseraPlusOrLiteCta);
